import { AnchorCss } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import { createGlobalStyle } from 'styled-components';
import { borders } from '../../fuse-borders/src/borders';
import colours from '../../fuse-colours/src/colours';
import { elevation } from '../../fuse-elevation/src/elevation';
import { gridConfig } from '../../fuse-responsive-grid/src/grid-config';
import {
	globalSpacingTokens,
	spacing as oldSpacingTokens,
	pageSpacingTokens,
} from '../../fuse-spacing/src/spacing';
import { defaultCssProps, display, heading, paragraph } from '../../fuse-typography/src/typography';
import { themesConfig } from './themes-config';

const validBrands = ['very', 'littlewoods', 'default'];

const propTypes = {
	brand: PropTypes.oneOf(validBrands),
};
const defaultProps = {
	brand: 'very',
};

/* Font Family  */
const createBrandFontFamilyCss = (brand) => `--font-brand: ${themesConfig[brand].fontFamily};`;
/* Base Font Size */
const baseFontSizeCss = `--font-base-size: 62.5%;`;

/**
 * Generates the typography (display, heading and paragraph) CSS based on the typography object from @tvg/fuse-typography
 * @param {string} prefix
 * @param {object} typoObj
 * @returns {string} typography css
 */
const generateTypographyCustomProperties = (brand) => {
	const displayFontWeight = `--font-weight-display: ${display(brand).fontWeight};`; // displayFontWeight
	const headingFontWeight = `--font-weight-heading: ${heading(brand).fontWeight};`; // headingFontWeight
	const paragraphFontWeight = `--font-weight-paragraph: ${paragraph(brand).fontWeight};`; // paragraphFontWeight

	const displayFontColor = `--color-font-display: ${display(brand).fontColor};`; // displayFontColor
	const headingFontColor = `--color-font-heading: ${heading(brand).fontColor};`; // headingFontColor
	const paragraphFontColor = `--color-font-paragraph: ${paragraph(brand).fontColor};`; // paragraphFontColor

	const typographyCustomProps = [
		displayFontWeight,
		paragraphFontWeight,
		headingFontWeight,
		displayFontColor,
		paragraphFontColor,
		headingFontColor,
	].join('');

	const typographySizeCustomProps = Object.keys(defaultCssProps)
		.map(
			(key) =>
				`--font-size-${key.replace('t', '')}: calc(${defaultCssProps[key].fontSize} * 1.6);
				--font-size-em-${key.replace('t', '')}: calc(${defaultCssProps[key].fontSize.replace(
					'rem',
					'em',
				)} * 1.6);
				--font-line-height-${key.replace('t', '')}: calc(${defaultCssProps[key].lineHeight} * 1.6);
				--font-line-height-em-${key.replace('t', '')}: calc(${defaultCssProps[key].lineHeight.replace(
					'rem',
					'em',
				)} * 1.6);
				--spacing-paragraph-margin-bottom-${key.replace('t', '')}: ${defaultCssProps[key].marginBottom};
				--spacing-paragraph-margin-bottom-em-${key.replace('t', '')}: ${defaultCssProps[
					key
				].marginBottom.replace('rem', 'em')};`,
		)
		.join('')
		.replace(' ', '');

	const sortedTypographyVariables = (typographyCustomProps + typographySizeCustomProps)
		.split(';')
		.sort()
		.join(';');

	return `${sortedTypographyVariables};`;
};

/* Old Spacing Tokens - to be removed upon decommissioning */
const createOldSpacingCustomProperties = () =>
	Object.keys(oldSpacingTokens)
		.map((key) => `--spacing-${key}: calc(${oldSpacingTokens[key]} * 1.6);`)
		.join('');

/* New Spacing Tokens discernable from the old takens with the -global prefix */
const createNewSpacingCustomProperties = () =>
	Object.keys(globalSpacingTokens)
		.map((key) => `--spacing-global-${key}: calc(${globalSpacingTokens[key]} * 1.6);`)
		.join('');

/* Page Spacing Tokens */
const createPageSpacingCustomProperties = () =>
	Object.keys(pageSpacingTokens)
		.map((key) => `--spacing-page-${key}: calc(${pageSpacingTokens[key]} * 1.6);`)
		.join('');

// Iterate through themesConfig brand color object and create a string consisting of css vars for colors
const createBrandColoursCustomProperties = (brand) =>
	Object.keys(themesConfig[brand].color).reduce(
		(acc, currentItem) =>
			`${acc}--color-brand-${currentItem}: ${themesConfig[brand].color[currentItem]};`,
		'',
	);

const createColoursCustomProperties = () =>
	Object.keys(colours)
		.map((group) =>
			Object.keys(colours[group]) // loop through first level
				.map((colour) => `--color-${group}-${colour}: ${colours[group][colour]}; `) // loop through values
				.join(''),
		)
		.join('');

const createElevationCustomProperties = () =>
	Object.keys(elevation)
		.map((group) =>
			Object.keys(elevation[group]).reduce((acc, currentToken) => {
				// Check if it's old token, if so amend the css variable string
				const oldToken = /[0-9]/g.test(currentToken);
				const cssVar = oldToken
					? `--elevation-${group}-${currentToken}: ${elevation[group][currentToken]};`
					: `--elevation-${currentToken}: ${elevation[group][currentToken]};`;
				// Return accumulator with all CSS variable strings
				return acc + cssVar;
			}, ''),
		)
		.join('');

const createBorderCustomProperties = () =>
	Object.keys(borders)
		.map((properties) =>
			Object.keys(borders[properties]) // loop through border radius and border width
				.map((size) => `--border-${properties}-${size}: ${borders[properties][size]};`) // loop through the sizes
				.join(''),
		)
		.join('');

export const createmarkdownStyles = (brand) => {
	const { defaultHeading } = themesConfig[brand];

	/**
	 * This generates CSS for h1 to h6 i.e. h1.markdown__h1, h2.markdown__h2, etc.
	 * @param {string} size - This is directly related to typography config heading sizes from M3 (smallest) - 14 (largest)
	 * @returns Specific CSS string that outputs styles related to heading size
	 */
	const setHeadingCSS = (size) => {
		const color = `var(--color-font-heading)`;
		const fontSize = `var(--font-size-${size.toLowerCase()})`;
		const fontWeight = `var(--font-weight-heading)`;
		const lineHeight = `var(--font-line-height-${size.toLowerCase()})`;
		const marginBottom = `var(--spacing-paragraph-margin-bottom-${size.toLowerCase()})`;

		return `
			color: ${color};
			font-size: ${fontSize};
			font-weight: ${fontWeight};
			line-height: ${lineHeight};
			margin-bottom: ${marginBottom};`;
	};

	const renderViewportHeadingCSS = (viewports, headingTags) =>
		viewports
			.map(
				(viewport) => `@media screen and (min-width: ${gridConfig[viewport].minWidth}) {
					${headingTags.map(
						(tag) => `
						${tag}.markdown__${tag} {
							${setHeadingCSS(defaultHeading[viewport][tag])}
						}`,
					)}
				}`,
			)
			.join('');

	return `
		${renderViewportHeadingCSS(Object.keys(defaultHeading), ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])}	


		p.markdown__typography-paragraph {
			color: var(--color-font-paragraph);
			font-size: var(--font-size-1);
			font-weight: var(--font-weight-paragraph);
			line-height: var(--font-line-height-1);
			margin-bottom: var(--spacing-paragraph-margin-bottom-1);
		}

		em.markdown__color-brand-mid {
			color: var(--color-brand-mid);
			font-style: normal;
		}

		strong.markdown__typography-bold {
			font-weight: var(--font-weight-heading); // 700 as bold
		}

		a.markdown__link {
			${AnchorCss}
		}

		ol.markdown__ordered-list {
			list-style-type: decimal; 
			padding: 0 0 0 var(--spacing-global-space6)
		}

		ul.markdown__unordered-list {
			list-style-type: disc; 
			padding: 0 0 0 var(--spacing-global-space6)
		}

		li.markdown__ordered-list-item,
		li.markdown__unordered-list-item { 
			color: var(--Grey-grey-900);
			font-size: var(--font-size-1);
			font-weight: var(--font-weight-paragraph);
			line-height: var(--font-line-height-1);
		}
	`;
};

const GlobalTheme = createGlobalStyle`
    ${({ brand = 'very', renderGlobalResetStyles = true }) => {
			const globalStyles = renderGlobalResetStyles
				? `html{box-sizing:border-box;}*,:after,:before{box-sizing:inherit}:focus{outline:1px dotted #3a64cb}body,caption,fieldset,h1,h2,h3,h4,h5,h6,ol,p,ul{margin:0;padding:0}ol,ul{list-style:none}img{max-width:100%;height:auto}fieldset,iframe{border:0}button,button:active{color:unset}
			.hidden,#speedtrap {display: none}`
				: '';

			const fuseBodyStyles = renderGlobalResetStyles
				? `html{font-size: var(--font-base-size)}
				body {color: ${themesConfig[brand].baseFontColour};font-display: swap;font-family: var(--font-brand), Arial, Helvetica, sans-serif;} input, textarea, select, button {font-family: inherit;}`
				: '';

			return `
            /* This is the legacy reset - needs a 2022 update */
            ${globalStyles}

            :root {
				${createBrandFontFamilyCss(brand)}
				${baseFontSizeCss}
				${generateTypographyCustomProperties(brand)}
				/* Old spacing tokens below to be removed upon decommissioning */
				${createOldSpacingCustomProperties()}
				${createNewSpacingCustomProperties()}
				${createPageSpacingCustomProperties()}
				${createBrandColoursCustomProperties(brand)}
				${createColoursCustomProperties()}
				${createElevationCustomProperties()}
				${createBorderCustomProperties()}
            }

			@font-face {
				font-family: 'Very Sans';
				src: url('/sa/fe-global-assets/fonts/VerySans-Medium.woff');
				font-weight: ${paragraph(brand).fontWeight}; // referenced as 500 in Figma
				font-display: swap;
			}
			@font-face {
				font-family: 'Very Sans';
				src: url('/sa/fe-global-assets/fonts/VerySans-Bold.woff');
				font-weight: ${heading(brand).fontWeight};
				font-display: swap;
			}
			@font-face {
				font-family: 'Very Sans';
				src: url('/sa/fe-global-assets/fonts/VerySans-Headline.woff');
				font-weight: ${display(brand).fontWeight}; // referenced as 400 in Figma
				font-display: swap;
			}

            ${fuseBodyStyles}
			${createmarkdownStyles(brand)}
		`;
		}}
`;

function GlobalThemeComponent({ brand,  ...props }) {
	return <GlobalTheme brand={validBrands.includes(brand) ? brand : 'very'} {...props} />;
}

GlobalThemeComponent.propTypes = propTypes;
GlobalThemeComponent.defaultProps = defaultProps;

export default GlobalThemeComponent;
